import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';

export type TabType = 'chat' | 'stocks' | 'pulse';

interface TabBarProps {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  unreadCount?: number;
}

/**
 * TabBar - iOS Segmented Control Style
 * Top-positioned navigation with smooth pill indicator
 */
export const TabBar: React.FC<TabBarProps> = ({ activeTab, onTabChange, unreadCount = 0 }) => {
  const tabs: { id: TabType; label: string; icon: string; activeIcon: string }[] = [
    { id: 'chat', label: 'Chat', icon: 'chatbubbles-outline', activeIcon: 'chatbubbles' },
    { id: 'stocks', label: 'Stocks', icon: 'trending-up-outline', activeIcon: 'trending-up' },
    { id: 'pulse', label: 'Pulse', icon: 'pulse-outline', activeIcon: 'pulse' },
  ];

  return (
    <View style={styles.container}>
      {tabs.map(tab => {
        const isActive = activeTab === tab.id;
        return (
          <TouchableOpacity
            key={tab.id}
            style={[styles.tab, isActive && styles.tabActive]}
            onPress={() => onTabChange(tab.id)}
            activeOpacity={0.7}
          >
            <Ionicons
              name={isActive ? (tab.activeIcon as any) : (tab.icon as any)}
              size={20}
              color={isActive ? Colors.accent.primary : Colors.text.tertiary}
            />
            <Text style={[styles.tabLabel, isActive && styles.tabLabelActive]}>{tab.label}</Text>
            {tab.id === 'chat' && unreadCount > 0 && (
              <View style={styles.badge}>
                <Text style={styles.badgeText}>{unreadCount > 99 ? '99+' : unreadCount}</Text>
              </View>
            )}
          </TouchableOpacity>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    backgroundColor: Colors.background.secondary,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
    paddingVertical: 6,
    paddingHorizontal: 12,
    gap: 4,
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 5,
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 8,
    position: 'relative',
    // Smooth transition for background
    ...Platform.select({
      ios: {
        // Add subtle transition effect
        transition: 'all 0.15s ease-out',
      },
    }),
  },
  tabActive: {
    backgroundColor: `${Colors.accent.primary}20`,
    // iOS-style glow effect for active tab
    ...Platform.select({
      ios: {
        shadowColor: Colors.accent.primary,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.25,
        shadowRadius: 4,
      },
      android: {
        elevation: 2,
      },
    }),
  },
  tabLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: Colors.text.tertiary,
    letterSpacing: 0.2,
  },
  tabLabelActive: {
    color: Colors.accent.primary,
    fontWeight: '700',
    letterSpacing: 0.3,
  },
  badge: {
    position: 'absolute',
    top: 6,
    right: 6,
    backgroundColor: Colors.status.error,
    borderRadius: 10,
    minWidth: 18,
    height: 18,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 5,
  },
  badgeText: {
    fontSize: 10,
    fontWeight: '700',
    color: Colors.text.primary,
  },
});
